import type { AuditEntry } from '../lib/api';
import type { IconName } from '../ui/icons';

/**
 * Renders a journal entry as one Russian sentence.
 *
 * The log is read by people, not machines: "Мария Иванова удалила 14 объектов
 * на доске «Алгебра 9»" answers the question in one line, where an action
 * code plus five id columns would need decoding first. The record carries the
 * names it needs, so nothing here has to look anything up.
 *
 * Two Russian-specific concessions. Verbs take the "удалил(а)" form, because
 * an account has a name but no grammatical gender. And a person the action was
 * done *to* goes after a colon rather than into the sentence, since
 * "назначил Тимур Учитель" would need the accusative case of a name we cannot
 * decline.
 */

function q(value: string): string {
  return value ? `«${value}»` : '';
}

function count(n: number, one: string, few: string, many: string): string {
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 14) return `${n} ${many}`;
  const mod10 = n % 10;
  if (mod10 === 1) return `${n} ${one}`;
  if (mod10 >= 2 && mod10 <= 4) return `${n} ${few}`;
  return `${n} ${many}`;
}

const KIND_LABEL: Record<string, string> = {
  teacher: 'учитель',
  curator: 'куратор',
  student: 'ученик',
  нет: 'без роли',
};

const ROLE_LABEL: Record<string, string> = {
  admin: 'владелец',
  member: 'участник',
  editor: 'редактор',
  commenter: 'комментатор',
  viewer: 'только просмотр',
};

function label(map: Record<string, string>, value: unknown): string {
  const key = String(value);
  return map[key] ?? key;
}

/** The action half of the sentence — the actor's name is prepended by the caller. */
export function auditAction(entry: AuditEntry): string {
  const meta = entry.meta ?? {};
  const board = q(entry.boardName);
  const org = q(entry.orgName);
  const who = String(meta.user ?? '');

  switch (entry.action) {
    case 'org.create':
      return `создал(а) организацию ${org}${meta.owner ? ` · владелец: ${meta.owner}` : ''}`;
    case 'org.rename':
      return `переименовал(а) организацию ${q(String(meta.from ?? ''))} в ${q(String(meta.to ?? ''))}`;
    case 'org.delete':
      return `удалил(а) организацию ${org}${
        Number(meta.boards ?? 0) > 0
          ? ` вместе с ${count(Number(meta.boards), 'доской', 'досками', 'досками')}`
          : ''
      }`;
    case 'org.owner_set':
      return `назначил(а) владельца организации ${org}: ${who}`;
    case 'member.join':
      return `присоединился(ась) к организации ${org}`;
    case 'member.role':
      return `изменил(а) роль в ${org} на «${label(ROLE_LABEL, meta.role)}»: ${who}`;
    case 'member.kind':
      return `назначил(а) роль «${label(KIND_LABEL, meta.kind)}»: ${who}`;
    case 'member.payment':
      return `отметил(а) оплату (${meta.paid === 'да' ? 'оплачено' : 'не оплачено'}${
        meta.amount !== undefined ? `, ${meta.amount}` : ''
      }${meta.date ? `, от ${String(meta.date)}` : ''}): ${who}`;
    case 'member.remove':
      return `убрал(а) из организации ${org}: ${who}`;
    case 'invite.create':
      return entry.boardId
        ? `создал(а) приглашение на доску ${board}`
        : `создал(а) приглашение в ${org}`;
    case 'invite.revoke':
      return entry.boardId
        ? `отозвал(а) приглашение на доску ${board}`
        : `отозвал(а) приглашение в ${org}`;
    case 'board.create':
      return `создал(а) доску ${board}`;
    case 'board.rename':
      return `переименовал(а) доску ${q(String(meta.from ?? ''))} в ${q(String(meta.to ?? ''))}`;
    case 'board.move':
      return `перенёс(ла) доску ${board} из ${q(String(meta.from ?? ''))} в ${org}`;
    case 'board.block':
      return `переместил(а) доску ${board} в блок ${q(String(meta.block ?? ''))}`;
    case 'board.delete':
      return `удалил(а) доску ${board}`;
    case 'board.member_add':
      return `дал(а) доступ к доске ${board} («${label(ROLE_LABEL, meta.role)}»): ${who}`;
    case 'board.member_remove':
      return `забрал(а) доступ к доске ${board}: ${who}`;
    case 'board.snapshot_restore':
      return `восстановил(а) доску ${board} из сохранённой версии`;
    case 'board.nodes_added':
      return `добавил(а) ${count(
        Number(meta.count ?? 0),
        'объект',
        'объекта',
        'объектов',
      )} на доске ${board}`;
    case 'board.nodes_deleted':
      return `удалил(а) ${count(
        Number(meta.count ?? 0),
        'объект',
        'объекта',
        'объектов',
      )} на доске ${board}`;
    case 'board.cleared':
      return `очистил(а) доску ${board} — удалено ${count(
        Number(meta.count ?? 0),
        'объект',
        'объекта',
        'объектов',
      )}`;
    case 'task.create':
      return `создал(а) задачу ${q(String(meta.title ?? ''))} для ${count(
        Number(meta.students ?? 0),
        'ученика',
        'учеников',
        'учеников',
      )}`;
    case 'task.update':
      return `изменил(а) задачу ${q(String(meta.title ?? ''))}${
        meta.author ? ` (автор: ${String(meta.author)})` : ''
      }`;
    case 'task.delete':
      return `удалил(а) задачу ${q(String(meta.title ?? ''))}`;
    case 'task.solution_board':
      return `создал(а) доску решения для ${q(String(meta.title ?? ''))}`;
    case 'call.start':
      return `начал(а) звонок на доске ${board}`;
    case 'call.stop':
      return `завершил(а) звонок на доске ${board}${
        meta.minutes !== undefined ? ` (${meta.minutes} мин)` : ''
      }`;
    case 'call.mute':
      return `${meta.muted === 'да' ? 'заглушил(а)' : 'снял(а) глушение'} в звонке на доске ${board}: ${who}`;
    case 'call.kick':
      return `исключил(а) из звонка на доске ${board}: ${who}`;
    case 'call.ban':
      return `закрыл(а) доступ к звонку на доске ${board}: ${who}${
        meta.until && meta.until !== 'навсегда' ? ' (временно)' : ''
      }`;
    case 'call.unban':
      return `вернул(а) доступ к звонку на доске ${board}: ${who}`;
    case 'chat.mute':
      return `запретил(а) писать в чат на доске ${board}: ${who}${
        meta.until && meta.until !== 'навсегда' ? ' (временно)' : ''
      }`;
    case 'chat.unmute':
      return `разрешил(а) писать в чат на доске ${board}: ${who}`;
    case 'group.create':
      return `создал(а) группу ${q(String(meta.group ?? ''))}`;
    case 'group.update':
      return `изменил(а) группу ${q(String(meta.group ?? ''))}`;
    case 'group.delete':
      return `удалил(а) группу ${q(String(meta.group ?? ''))}`;
    default:
      return entry.action;
  }
}

export function auditSentence(entry: AuditEntry): string {
  return `${entry.actorName} ${auditAction(entry)}`;
}

/** Deletions are the entries anyone comes to this screen for; they read red. */
export function auditTone(action: string): 'danger' | 'warning' | 'normal' {
  if (action === 'org.delete' || action === 'board.delete' || action === 'board.cleared') {
    return 'danger';
  }
  if (action === 'board.nodes_deleted' || action === 'member.remove') return 'warning';
  return 'normal';
}

export function auditIcon(action: string): IconName {
  if (action.startsWith('org.')) return 'org';
  if (action.startsWith('member.')) return 'users';
  if (action.startsWith('invite.')) return 'link';
  if (action.startsWith('task.')) return 'clipboard';
  if (action.startsWith('call.')) return 'phone';
  if (action.startsWith('chat.')) return 'comment';
  if (action.startsWith('group.')) return 'users';
  if (action === 'board.cleared' || action === 'board.delete' || action === 'board.nodes_deleted') {
    return 'trash';
  }
  return 'boards';
}

/** Filter options offered in the journal's dropdown. */
export const AUDIT_FILTERS: { value: string; label: string }[] = [
  { value: '', label: 'Все действия' },
  { value: 'board.delete', label: 'Удаление досок' },
  { value: 'board.cleared', label: 'Очистка досок' },
  { value: 'board.nodes_deleted', label: 'Удаление объектов' },
  { value: 'board.nodes_added', label: 'Рисование' },
  { value: 'org.delete', label: 'Удаление организаций' },
  { value: 'board.create', label: 'Создание досок' },
  { value: 'member.remove', label: 'Исключение участников' },
  { value: 'member.payment', label: 'Оплата' },
  { value: 'task.create', label: 'Задачи' },
];
